import ImageNotSupportedOutlined from '@mui/icons-material/ImageNotSupportedOutlined';
import Box from '@mui/material/Box';
import { type ReactNode, useRef, useState } from 'react';

type ResponsiveSize = number | (number | null)[] | { [key: string]: number | null };

interface MediaThumbProps {
  size?: ResponsiveSize;
  width?: ResponsiveSize | undefined;
  src?: string | null;
  previewUrl?: string | null;
}

function scaleSize(size: ResponsiveSize, factor: number): ResponsiveSize {
  if (typeof size === 'number') return size * factor;
  if (Array.isArray(size)) return size.map((value) => (value == null ? value : value * factor));
  return Object.fromEntries(
    Object.entries(size).map(([key, value]) => [key, value == null ? value : value * factor]),
  );
}

export function MediaThumb({ size = 36, width, src, previewUrl }: MediaThumbProps): ReactNode {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isHovering, setIsHovering] = useState(false);
  const [thumbnailFailed, setThumbnailFailed] = useState(false);
  const thumbnail = thumbnailFailed ? null : src;

  function handleMouseEnter(): void {
    if (!previewUrl) return;
    setIsHovering(true);
    const video = videoRef.current;
    if (!video) return;
    video.play().catch(() => setIsHovering(false));
  }

  function handleMouseLeave(): void {
    setIsHovering(false);
    const video = videoRef.current;
    if (video) {
      video.pause();
      video.currentTime = 0;
    }
  }

  return (
    <Box
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        position: 'relative',
        width: width ?? size,
        height: size,
        flexShrink: 0,
        borderRadius: 1,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.page',
      }}
    >
      {thumbnail ? (
        <Box
          component="img"
          src={thumbnail}
          alt=""
          data-print-thumbnail="true"
          onError={() => setThumbnailFailed(true)}
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : previewUrl ? null : (
        <ImageNotSupportedOutlined
          sx={{ fontSize: scaleSize(size, 0.35), color: 'text.disabled' }}
        />
      )}
      {previewUrl && (
        <Box
          component="video"
          ref={videoRef}
          src={previewUrl}
          muted
          loop
          playsInline
          preload="metadata"
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: isHovering || !thumbnail ? 1 : 0,
            transition: 'opacity 150ms ease',
            pointerEvents: 'none',
          }}
        />
      )}
    </Box>
  );
}
